import {
  type PropsWithChildren,
  type Ref,
  type SetStateAction,
  useCallback,
  useEffect,
} from 'react';
import { useDrop } from 'react-dnd';

import styled from '@emotion/styled';
import { useTranslation } from '@kc-console/core';
import { IcChevronsRightSmallRegular } from '@kep/kcds-icons';

import { IconServiceButton } from '../ServiceButton';
import { type DropSide, FAVORITE_DRAG_TYPE, type FavoriteDragItem } from './constants';

interface FavoriteMoreButtonContainerProps extends PropsWithChildren {
  /** 더보기 영역(숨김 목록)의 첫 항목 전역 인덱스 = 노출목록↔더보기 경계 gap */
  boundaryIndex: number;
  ref: Ref<HTMLButtonElement>;
  setIsMoreMenuOpen: (value: SetStateAction<boolean>) => void;
  onDragHover?: (from: number, to: number, side: DropSide) => void;
}

const FavoriteMoreButtonContainer = ({
  boundaryIndex,
  children,
  onDragHover,
  ref,
  setIsMoreMenuOpen,
}: FavoriteMoreButtonContainerProps) => {
  const { t } = useTranslation();

  // 드래그 중 "더보기" 버튼 위로 호버하면 MoreMenu 자동 열기 +
  // 삽입 위치를 경계(노출목록 끝 = 더보기 첫 항목 앞)로 설정한다.
  // 버튼은 drop 위치를 갱신하지 않는 사각지대였으므로, 호버만으로도 경계 indicator를 표시하고
  // 버튼/주변 빈 영역에 떨어뜨려도 경계로 커밋되도록 한다.
  const [, dropRef] = useDrop<FavoriteDragItem>({
    accept: FAVORITE_DRAG_TYPE,
    hover: dragItem => {
      setIsMoreMenuOpen(true);
      onDragHover?.(dragItem.index, boundaryIndex, 'before');
    },
    // drop을 정의하지 않으면 dnd-core가 innermost인 이 타깃의 결과를 빈 객체({})로 채워
    // didDrop()=true가 되고, body catch-all이 양보(undefined 반환)해 dropResult에 dropped가
    // 없어 커밋되지 않는다. 버튼 위 드롭은 hover로 지정된 경계 위치로 직접 커밋한다.
    drop: () => ({ dropped: true }),
  });

  const handleRef = useCallback(
    (element: HTMLButtonElement | null) => {
      dropRef(element);
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    },
    [ref, dropRef],
  );

  useEffect(() => {
    return () => {
      setIsMoreMenuOpen(false);
    };
  }, [setIsMoreMenuOpen]);

  return (
    <>
      <MoreButton
        ref={handleRef}
        aria-label={t('ui:action.show_more')}
        onClick={e => {
          e.stopPropagation();
          setIsMoreMenuOpen(prev => !prev);
        }}
      >
        <IcChevronsRightSmallRegular />
      </MoreButton>
      {children}
    </>
  );
};

export default FavoriteMoreButtonContainer;

const MoreButton = styled(IconServiceButton)`
  flex-shrink: 0;
  min-width: 48px;
`;
